<template>
  <VanPopup
    :show="show"
    position="bottom"
    @update:show="emit('update:show', $event)"
    @close="emit('update:show', false)"
    class="h-full"
    :close-on-click-overlay="false"
  >
    <SpriteEditor
      v-if="show"
      :image-urls="imageUrls"
      @onConfirm="handleExport"
      @onClose="emit('update:show', false)"
      :frame-index="frameIndex"
    />
  </VanPopup>
</template>

<script lang="ts" setup>
import { useMedia } from "@/stores/media";
import SpriteEditor from "./SpriteEditor.vue";
import { useMate } from "@/stores/mate";
import { showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { Media } from "@/types/entity";

defineProps<{
  show: boolean;
  frameIndex?: number;
  imageUrls: string[];
}>();

const mateStore = useMate();
const { t } = useI18n();

const emit = defineEmits<{
  (e: "onConfirm", media: Media): void;
  (e: "update:show", show: boolean): void;
}>();

const handleExport = async (files: File[]) => {
  const loading = showLoadingToast(t(`Message.saving`));

  try {
    // Create a canvas to combine the 4 images into 1
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;

    // Assuming each sprite frame is 48x96, the combined image will be 192x96
    canvas.width = 48 * files.length;
    canvas.height = 96;

    const imagePromises = files.map((file, index) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, index * 48, 0, 48, 96);
          resolve();
        };
        img.src = URL.createObjectURL(file);
      });
    });

    await Promise.all(imagePromises);

    const blob = await new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, "image/png");
    });

    const combinedFile = new File([blob], "combined-sprite.png", {
      type: "image/png",
    });

    const media = await useMedia().uploadMediaItem(
      { file: combinedFile, mediaId: 0 },
      mateStore.currentMate!.id,
      0
    );

    loading.close();
    emit("onConfirm", { id: media.mediaId, url: media.url! } as Media);
    emit("update:show", false);
  } catch (error) {
    console.error("Error combining images:", error);
    loading.close();
  }
};
</script>
